// Updated typeahead-location-input.component.ts with lodash.deburr

import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, ViewChild, ElementRef, AfterViewInit, Renderer2, ChangeDetectorRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgForOf, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { filter, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import deburr from 'lodash.deburr';

interface BaseOption {
  id: number;
  name: string;
  value?: string | number;
}

interface NormalizedOption extends BaseOption {
  _normalized?: string; // Pre-computed normalized name for performance
}

@Component({
  selector: 'app-typeahead-location-input',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    TranslateModule
  ],
  templateUrl: './typeahead-location-input.component.html',
  styleUrls: ['./typeahead-location-input.component.scss']
})
export class TypeaheadLocationInputComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() label: string = '';
  @Input() placeholder: string = 'Search...';
  @Input() control: FormControl = new FormControl();
  @Input() options: BaseOption[] = [];
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() type: 'country' | 'region' | 'city' = 'country';
  @Input() parentLocation: {country?: string, region?: string} = {};
  @Input() displayKey: string = 'name';
  @Input() isLoading: boolean = false;

  @ViewChild('inputElement') inputElement: ElementRef<HTMLInputElement>;

  @Output() optionSelected = new EventEmitter<BaseOption | null>();

  @Output() selectionCleared = new EventEmitter<{
    type: 'country' | 'region' | 'city'
  }>();

  @Output() dropdownToggled = new EventEmitter<void>();

  searchTerm: string = '';
  isDropdownOpen: boolean = false;
  selectedOption: BaseOption | null = null;
  private previousSearchTerm: string = '';

  // Store normalized options for performance
  private normalizedOptions: NormalizedOption[] = [];

  private destroy$ = new Subject<void>();

  // Document click-outside listener, active only while the dropdown is open
  private clickListener: (() => void) | null = null;

  // Generate a unique ID for each instance
  randomId: string = Math.random().toString(36).substring(2, 8);

  constructor(private renderer: Renderer2, private cdr: ChangeDetectorRef, private elRef: ElementRef) { }

  ngOnInit(): void {
    // Watch for control value changes
    this.control.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      if (typeof value === 'number' && value !== null) {
        const option = this.findOptionById(value);
        if (option) {
          this.selectedOption = option;
          this.searchTerm = option.name;
          this.previousSearchTerm = option.name;
        }
      } else if (value === null) {
        // Control was cleared
        this.selectedOption = null;
        this.searchTerm = '';
        this.previousSearchTerm = '';
      }
    });

    // Initialize selectedOption if control has a value
    if (this.control.value) {
      const option = this.findOptionById(this.control.value);
      if (option) {
        this.selectedOption = option;
        this.searchTerm = option.name;
        this.previousSearchTerm = option.name;
      }
    }

    // Watch for disabled state changes
    this.control.statusChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(status => {
      if (this.control.disabled) {
        this.closeDropdown();
      }
      this.cdr.detectChanges();
    });

    // Pre-normalize options if they exist
    if (this.options && this.options.length > 0) {
      this.normalizeOptions();
    }
  }

  ngOnChanges(changes: any): void {
    // Pre-compute normalized names when options change
    if (changes.options && this.options) {
      this.normalizeOptions();
      this.cdr.detectChanges();
    }

    // Reset when parent location changes
    if (changes.parentLocation && !changes.parentLocation.isFirstChange()) {
      const prev = changes.parentLocation.previousValue;
      const curr = changes.parentLocation.currentValue;

      if (JSON.stringify(prev) !== JSON.stringify(curr)) {
        this.searchTerm = '';
        this.previousSearchTerm = '';

        if (this.type === 'city' && this.control.value) {
          this.control.setValue(null);
          this.selectedOption = null;
        }
      }
    }

    // When control becomes disabled, clear the selection
    if (changes.disabled && changes.disabled.currentValue === true) {
      if (this.control.value) {
        this.control.setValue(null);
        this.selectedOption = null;
        this.searchTerm = '';
        this.previousSearchTerm = '';
      }
    }
  }

  ngAfterViewInit(): void {
    const inputElement = this.inputElement?.nativeElement;
    if (inputElement) {
      this.renderer.setAttribute(inputElement, 'autocomplete', `new-${this.randomId}`);
      this.renderer.setAttribute(inputElement, 'autocorrect', 'off');
      this.renderer.setAttribute(inputElement, 'autocapitalize', 'off');
      this.renderer.setAttribute(inputElement, 'spellcheck', 'false');
      this.renderer.setAttribute(inputElement, 'data-form-type', 'other');
      this.renderer.setAttribute(inputElement, 'data-lpignore', 'true');
      this.renderer.setAttribute(inputElement, 'data-autofill-prevent', 'true');
    }
  }

  /**
   * Pre-compute normalized names for all options (performance optimization)
   */
  private normalizeOptions(): void {
    if (!this.options) {
      this.normalizedOptions = [];
      return;
    }

    this.normalizedOptions = this.options.map(option => ({
      ...option,
      _normalized: deburr(option.name.toLowerCase())
    }));
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;
    this.searchTerm = newValue;
    this.openDropdown();

    // Check if user is deleting/modifying text of a selected option
    if (this.selectedOption) {
      // If the new value is different from the selected option's name
      // This includes cases where user is deleting characters
      if (newValue !== this.selectedOption.name) {
        // User is modifying a previously selected value
        this.selectedOption = null;
        this.control.setValue(null, { emitEvent: false });

        // Emit events to notify parent that selection was cleared
        this.optionSelected.emit(null);
        this.selectionCleared.emit({ type: this.type });
      }
    }

    this.previousSearchTerm = newValue;
  }

  onFocus(): void {
    this.openDropdown();
  }

  // Clicking the input only ever opens — covers the case where the input is
  // already focused but the dropdown was closed (e.g. after a chevron close),
  // since no focus event fires on an already-focused element.
  onInputClick(): void {
    this.openDropdown();
  }

  // Every press inside the component is mousedown-shielded (dropdown container,
  // toggle and clear buttons), so blur only fires when focus genuinely leaves —
  // safe to close synchronously, no timer needed.
  onBlur(): void {
    this.closeDropdown();
  }

  toggleDropdown(): void {
    if (this.control.disabled) {
      return;
    }
    if (this.isDropdownOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  selectOption(option: BaseOption): void {
    this.control.setValue(option.id);
    this.searchTerm = option.name;
    this.previousSearchTerm = option.name;
    this.selectedOption = option;
    this.closeDropdown();
    this.optionSelected.emit(option);

    // Release focus so stray keystrokes can't wipe the fresh selection;
    // re-clicking the input refocuses it and reopens the list via onFocus.
    this.inputElement?.nativeElement.blur();
  }

  clearSelection(): void {
    this.control.setValue(null);
    this.searchTerm = '';
    this.previousSearchTerm = '';
    this.selectedOption = null;
    this.openDropdown();

    this.optionSelected.emit(null);
    this.selectionCleared.emit({ type: this.type });

    this.inputElement?.nativeElement.focus();
  }

  private openDropdown(): void {
    if (this.control.disabled || this.isDropdownOpen) {
      return;
    }
    this.isDropdownOpen = true;
    this.addClickOutsideListener();
    this.dropdownToggled.emit();
  }

  private closeDropdown(): void {
    this.removeClickOutsideListener();
    if (!this.isDropdownOpen) {
      return;
    }
    this.isDropdownOpen = false;

    // Reconcile the visible text with the actual selection on every close:
    // restore the selected option's name, or clear typed-but-unselected text.
    if (this.selectedOption && this.searchTerm !== this.selectedOption.name) {
      this.searchTerm = this.selectedOption.name;
    } else if (!this.selectedOption && this.searchTerm !== '') {
      this.searchTerm = '';
    }
  }

  private addClickOutsideListener(): void {
    if (this.clickListener) {
      return;
    }
    this.clickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!this.elRef.nativeElement.contains(target)) {
        this.closeDropdown();
      }
    });
  }

  private removeClickOutsideListener(): void {
    if (this.clickListener) {
      this.clickListener();
      this.clickListener = null;
    }
  }

  getSelectedOptionName(): string {
    return this.selectedOption ? this.selectedOption.name : '';
  }

  /**
   * Gets filtered options with diacritics-insensitive search using lodash.deburr
   * Uses pre-computed normalized values for maximum performance
   * @returns Filtered array of options
   */
  getFilteredOptions(): BaseOption[] {
    if (!this.normalizedOptions || this.normalizedOptions.length === 0) {
      return [];
    }

    if (!this.searchTerm || this.searchTerm === '') {
      return this.normalizedOptions;
    }

    // Normalize the search term once
    const normalizedSearchTerm = deburr(this.searchTerm.toLowerCase());

    // Filter using pre-computed normalized values (very fast)
    return this.normalizedOptions.filter(option => {
      // Use the pre-computed normalized name
      return option._normalized!.includes(normalizedSearchTerm);
    });
  }

  private findOptionById(id: number): BaseOption | null {
    // Search in normalized options first, fallback to regular options
    const options = this.normalizedOptions.length > 0 ? this.normalizedOptions : this.options;
    return options.find(o => o.id === id) || null;
  }

  ngOnDestroy(): void {
    this.removeClickOutsideListener();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showLoading(): boolean {
    return this.isLoading && this.isDropdownOpen;
  }

  get showNoResults(): boolean {
    return !this.isLoading && this.isDropdownOpen && this.getFilteredOptions().length === 0;
  }
}
